import ScreenKey from '@/Enums/Screens/ScreenKey.enum';
import type { Role } from '@/Enums/Users/Role.enum';
import type IScreenAccess from '@/Interfaces/Users/ScreenAccess.interface';
import type { ITabGroup } from '@/Interfaces/Users/TabGroup.interface';
import {
  isAdminRole,
  isRoleAllowedClientSelect,
  isRoleAllowedWhitelabelSelect,
  isSuperAdminRole,
} from '@/helpers/roles';
import type { RootState } from '@/store';
import { createSelector } from '@reduxjs/toolkit';

export const me = (state: RootState) => state.me;

export const selectMeScreenAccess = (screenName: ScreenKey) =>
  createSelector([me], ({ screens }) => {
    const screen = screens.find(s => s.name === screenName);
    return screen?.access === 'full' ? false : true;
  });

export const selectMeRole = createSelector([me], ({ role }) => role as Role);
export const selectHideDealsInImpressionsByType = createSelector(
  [me],
  ({ hideDealsInImpressionsByType }) => hideDealsInImpressionsByType,
);
export const selectMeLoading = createSelector([me], ({ loading }) => loading);
export const selectMeScreens = createSelector([me], ({ screens }) => screens);

export const selectIsAdmin = createSelector([me], ({ role }) => isAdminRole(role));
/** Ribeye admins only — `selectIsAdmin` also matches org and whitelabel admins. */
export const selectIsSuperAdmin = createSelector([me], ({ role }) => isSuperAdminRole(role));
export const selectIsRibeyeMediaUser = createSelector([me], ({ userProfile }) =>
  userProfile?.email?.includes('ribeye.media'),
);
export const selectHideWhitelabels = createSelector([selectMeRole], meRole => !isRoleAllowedWhitelabelSelect(meRole));
export const selectHideClients = createSelector([selectMeRole], meRole => !isRoleAllowedClientSelect(meRole));

export const selectAllowCampaignApproval = createSelector(
  [me],
  ({ canApproveCampaigns, role }) => canApproveCampaigns || isSuperAdminRole(role),
);

export interface IGroupedScreens {
  id: number;
  name: string;
  screens: IScreenAccess[];
}

export const selectTabGroups = createSelector([me], ({ tabGroups }) => tabGroups);

export const selectGroupedScreens = createSelector(
  [me],
  ({ screens, tabGroups, ungroupedScreens }): { groups: IGroupedScreens[]; ungrouped: IScreenAccess[] } => {
    const hiddenScreens = [ScreenKey.FORECASTER_AI];
    const visibleScreens = screens.filter(s => !hiddenScreens.includes(s.name));
    const screenNameSet = new Set(visibleScreens.map(s => s.name));

    const groups: IGroupedScreens[] = tabGroups
      .map((group: ITabGroup) => ({
        id: group.id,
        name: group.name,
        screens: group.screens
          .filter(screenKey => screenNameSet.has(screenKey))
          .map(screenKey => visibleScreens.find(s => s.name === screenKey)!),
      }))
      .filter(group => group.screens.length > 0);

    const ungrouped = ungroupedScreens
      .filter(screenKey => screenNameSet.has(screenKey))
      .map(screenKey => visibleScreens.find(s => s.name === screenKey)!);

    return { groups, ungrouped };
  },
);
